import { useState } from 'react';
import { DEFAULT_TIME_ZONE, TIME_ZONE_KEY, fmt, setTimeZone } from '../format';
import { store } from '../session';
import { Icon, type IconName } from '../ui/icons';
import { useTheme, type ThemeChoice } from '../ui/theme';
import { DeskPrinterSetting } from './DeskPrinter';
import { NotificationSettings } from './Notifications';
import { Security } from './Security';

const CHOICES: readonly { value: ThemeChoice; label: string; hint: string; icon: IconName; preview: string }[] = [
  { value: 'dark', label: 'Gece', hint: 'Koyu zemin, sitedeki görünüm. Gözü yormaz, telefonda pili az harcar.', icon: 'moon', preview: '' },
  { value: 'light', label: 'Gündüz', hint: 'Açık zemin. Aydınlık depoda ve güneş alan masada daha rahat okunur.', icon: 'sun', preview: 'light' },
  { value: 'system', label: 'Cihaza göre', hint: 'Telefonun ya da bilgisayarın kendi gece/gündüz ayarını izler.', icon: 'monitor', preview: 'system' },
];

const ZONES: readonly { value: string; label: string }[] = [
  { value: 'Europe/London', label: 'Londra (İngiltere)' },
  { value: 'Europe/Berlin', label: 'Berlin (Orta Avrupa)' },
  { value: 'Europe/Athens', label: 'Atina (Doğu Avrupa)' },
  { value: 'Europe/Moscow', label: 'Moskova' },
  { value: 'Asia/Baku', label: 'Bakü' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'America/New_York', label: 'New York' },
  { value: 'UTC', label: 'UTC' },
];

/** The time zone this device shows times in: Türkiye by default, as Trendyol and Hepsiburada show them. */
function TimeZoneSettings() {
  const [choice, setChoice] = useState(() => store.get(TIME_ZONE_KEY) ?? '');
  const device = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const choose = (value: string) => {
    setChoice(value);
    setTimeZone(value || null);
    // every screen draws its times again in the new zone
    window.location.reload();
  };
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="clock" />
        <h2>Saat dilimi</h2>
        <span className="sub">Bu cihazda geçerli · şu an {fmt.dateTime(new Date().toISOString())}</span>
      </div>
      <div className="card-b stack">
        <div className="field" style={{ maxWidth: 420 }}>
          <label htmlFor="time-zone">Saatler bu dilimde gösterilsin</label>
          <select id="time-zone" className="select" value={choice} onChange={(e) => choose(e.target.value)}>
            <option value="">Türkiye (İstanbul, UTC+3) · önerilen</option>
            <option value="device">Bu cihazın saat dilimi ({device})</option>
            {ZONES.filter((z) => z.value !== DEFAULT_TIME_ZONE).map((z) => (
              <option key={z.value} value={z.value}>
                {z.label}
              </option>
            ))}
          </select>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Pazaryerleri saatleri Türkiye saatiyle gösterir; sipariş ve kargo saatlerinin onlarla aynı görünmesi için Türkiye seçili kalsın.
        </p>
      </div>
    </section>
  );
}

/** Ayarlar: how the panel looks on this device, which bildirimler reach this person, then sign-in security. */
export function Settings() {
  const [theme, setTheme] = useTheme();
  return (
    <div className="stack">
      <section className="card">
        <div className="card-h">
          <Icon name="sun" />
          <h2>Görünüm</h2>
          <span className="sub">Bu cihazda geçerli; telefonlar ve paketleme bilgisayarı ayrı ayarlanabilir</span>
        </div>
        <div className="card-b">
          <div className="theme-choices" role="radiogroup" aria-label="Görünüm">
            {CHOICES.map((c) => (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={theme === c.value}
                className={`theme-choice${theme === c.value ? ' on' : ''}`}
                onClick={() => setTheme(c.value)}
              >
                <span className={`theme-preview ${c.preview}`} aria-hidden>
                  {c.value === 'system' ? (
                    <>
                      <i className="p-main">
                        <span />
                        <span />
                      </i>
                      <i className="p-main">
                        <span />
                        <span />
                      </i>
                    </>
                  ) : (
                    <>
                      <i className="p-side" />
                      <i className="p-main">
                        <span />
                        <span />
                        <span />
                      </i>
                    </>
                  )}
                </span>
                <b>
                  <Icon name={c.icon} size={16} /> {c.label}
                </b>
                <small>{c.hint}</small>
              </button>
            ))}
          </div>
        </div>
      </section>
      <TimeZoneSettings />
      <DeskPrinterSetting />
      <NotificationSettings />
      <Security />
    </div>
  );
}
